'use client'

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Truck, Clock, CheckCircle, X, Package } from 'lucide-react'
import { api, useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import { getDeliveryBarStatus, getEffectiveTimelineKey, getTimelineSteps, getMobileTimelineSteps, TIMELINE_LABELS } from '../utils/orderTimeline'

const DeliveryStatusBar = () => {
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [recentOrder, setRecentOrder] = useState(null)
  const [loading, setLoading] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (!isAuthenticated) return

    const fetchRecentOrder = async () => {
      try {
        setLoading(true)
        const response = await api.get('/orders?limit=1')
        if (response.data && response.data.length > 0) {
          const order = response.data[0]
          // Only show if order is not delivered and not cancelled
          if (getEffectiveTimelineKey(order) !== 'delivered' && order.order_status !== 'cancelled') {
            setRecentOrder(order)
          }
        }
      } catch (error) {
        console.error('Failed to fetch recent order:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchRecentOrder()
  }, [isAuthenticated])

  if (!isAuthenticated || !recentOrder || dismissed) return null

  const statusInfo = getDeliveryBarStatus(recentOrder)
  const timelineKey = getEffectiveTimelineKey(recentOrder)
  const timelineSteps = isMobile ? getMobileTimelineSteps(recentOrder) : getTimelineSteps(recentOrder)

  const displayTimelineKey = isMobile && (timelineKey === 'placed' || timelineKey === 'in_process') ? 'confirmed' : timelineKey
  
  const mobileIcons = {
    placed: <Clock size={20} className="text-yellow-400" />,
    confirmed: <CheckCircle size={20} className="text-emerald-400" />,
    in_process: <Package size={20} className="text-orange-400" />,
    dispatched: <Truck size={20} className="text-sky-400" />,
    out_for_delivery: <Truck size={20} className="text-purple-400" />,
    delivered: <CheckCircle size={20} className="text-green-400" />,
  }
  
  const desktopIcons = {
    placed: <Clock size={20} className="text-yellow-400" />,
    confirmed: <CheckCircle size={20} className="text-emerald-400" />,
    in_process: <Package size={20} className="text-orange-400" />,
    dispatched: <Truck size={20} className="text-sky-400" />,
    out_for_delivery: <Truck size={20} className="text-purple-400" />,
    delivered: <CheckCircle size={20} className="text-green-400" />,
  }
  
  const statusIcon = (isMobile ? mobileIcons : desktopIcons)[displayTimelineKey] || <Clock size={20} className="text-yellow-400" />

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-r from-[#0a1a0f] via-[#050f0a] to-[#0a1a0f] border-t border-orange-500/30 shadow-2xl"
      >
        <div className="max-w-7xl mx-auto px-3 py-1.5 md:px-4 md:py-1.5">
          <div className="flex items-center justify-between gap-2 md:gap-4">
            <div className="flex items-center gap-2 md:gap-3 flex-1">
              <div className={`p-1.5 md:p-2 rounded-lg ${statusInfo.color} border flex items-center justify-center`}>
                {statusIcon}
              </div>
              <div className="flex-1">
                <p className={`${statusInfo.textColor} font-semibold text-[11px] md:text-sm`}>
                  {statusInfo.text}
                </p>
                <p className="text-gray-400 text-[10px] md:text-xs">
                  Order #{recentOrder.id.substring(0, 8).toUpperCase()} • {recentOrder.awb_number || recentOrder.tracking_id || 'Tracking ID: Pending'}
                  {recentOrder.shipment_status ? ` • ${recentOrder.shipment_status}` : ''}
                </p>
                {/* Complete Timeline */}
                <div className="mt-1.5 md:mt-2 flex items-center gap-1 overflow-x-auto pb-1">
                  {timelineSteps.map((step, idx) => (
                    <React.Fragment key={step.key}>
                      <div className="flex items-center gap-1 md:gap-1.5">
                        <div
                          className={`w-2 h-2 md:w-3 md:h-3 rounded-full border-2 ${
                            step.completed || step.current
                              ? 'bg-[#ffcc32] border-[#ffcc32] shadow-[0_0_8px_rgba(255,204,50,0.5)]'
                              : 'bg-[#0a1a0f] border-white/30'
                          }`}
                        />
                        <span
                          className={`text-[9px] md:text-[11px] font-semibold whitespace-nowrap ${
                            step.current ? 'text-white' : step.completed ? 'text-[#ffcc32]' : 'text-zinc-500'
                          }`}
                        >
                          {TIMELINE_LABELS[step.key] || step.label}
                        </span>
                      </div>
                      {idx < timelineSteps.length - 1 && (
                        <div className={`w-3 md:w-4 h-0.5 ${
                          step.completed ? 'bg-[#ffcc32]' : 'bg-white/20'
                        }`} />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1 md:gap-2">
              <button
                onClick={() => navigate('/orders')}
                className="px-3 py-1.5 md:px-4 md:py-2 bg-orange-500 text-black font-bold text-[11px] md:text-sm rounded-lg hover:bg-orange-600 transition"
              >
                Track Order
              </button>
              <button
                onClick={() => setDismissed(true)}
                className="p-1.5 md:p-2 text-gray-400 hover:text-white transition"
              >
                <X size={16} md={20} />
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}

export default DeliveryStatusBar
